import { DATE_FORMAT } from 'constants/date';
import { mapEntityParams } from 'constants/entities';
import { POSSIBLY_RELEVANT_VALUE, RELEVANT_VALUE } from 'constants/filters';
import { Entities } from 'store/slices/mapV2/tabsReducer/layersReducer/mapEntitiesSlice/types';
import { Counters, Hierarchy, TDateRange } from 'types';
import { MapEntityTypes } from 'types/map';

import {
  ActiveFilterCriteria,
  FilterCriteria,
} from 'components/ui/Filters/types';
import { getFormattedDate } from 'utils';

import { ActiveFilterCriteriasMap, EntityNodeData } from './types';

export const NODE_INDENT = 20;
export const LAYERS_UPDATE_DEBOUNCE = 300;

export const getFilterCriteriaParam = (criteria: ActiveFilterCriteria) => {
  const values = criteria.values.map((v) =>
    criteria.type === 'date'
      ? {
          gte: getFormattedDate((v.value as TDateRange)[0], DATE_FORMAT),
          lte: getFormattedDate((v.value as TDateRange)[1], DATE_FORMAT),
        }
      : String(v.value)
  );

  return {
    parameterID: criteria.id,
    values: values,
  };
};

export const getFilterCriteriaParams = (
  filterCriteriasMap: ActiveFilterCriteriasMap
) =>
  Object.values(filterCriteriasMap).map((criteria) =>
    getFilterCriteriaParam(criteria)
  );

export const getFolderEntityChildrenCount = (
  entities: Entities,
  id: string
) => {
  const entity = entities[id];

  return entity.childIDs.reduce((acc, curr): number => {
    const childEntity = entities[curr];

    if (childEntity?.info?.type === MapEntityTypes.OBJECT) {
      return childEntity.state.active ? acc + 1 : acc;
    }

    if (childEntity?.info?.type === MapEntityTypes.LAYER) {
      return acc + getFolderEntityChildrenCount(entities, String(curr));
    }

    return acc;
  }, 0);
};

export const getEntitiesTree = (
  entities: Entities,
  counters: Counters,
  hierarchy: Hierarchy,
  level = 0
): EntityNodeData[] =>
  hierarchy.children.reduce((acc, curr) => {
    const id = String(curr.id);
    const entity = entities[id];
    const parentId = String(entity?.parentIDs?.[0] ?? 0);
    const upmostParentId = String(entity?.upmostParentID ?? 0);
    const isFolder = entity?.info?.type === MapEntityTypes.LAYER;
    const isObject = entity?.info?.type === MapEntityTypes.OBJECT;

    if (isFolder) {
      const totalObjectCount = counters[id];
      const selectedObjectsCount = getFolderEntityChildrenCount(entities, id);
      const isSelected = !!(
        totalObjectCount && selectedObjectsCount === totalObjectCount
      );

      return [
        ...acc,
        {
          id: id,
          name: entity.entity.title,
          type: 'folder',
          isFolder: true,
          info: {
            level: level,
            parent: parentId,
            upmostParent: upmostParentId,
            stats: {
              selectedObjectsCount: selectedObjectsCount,
              totalObjectsCount: totalObjectCount,
            },
          },
          state: {
            storage: entity.state.storage,
            selected: isSelected,
          },
          entity: entity,
          children: getEntitiesTree(entities, counters, curr, level + 1),
        },
      ];
    }

    if (isObject) {
      return [
        ...acc,
        {
          id: id,
          name: entity.entity.title,
          type: 'object',
          isFolder: false,
          info: {
            level: level,
            parent: parentId,
            upmostParent: upmostParentId,
          },
          state: {
            storage: entity.state.storage,
            selected: entity.state.active,
          },
          entity: entity,
        },
      ];
    }

    return [...acc];
  }, [] as EntityNodeData[]);

export const getRootNodeCounter = (
  data: EntityNodeData[]
): [boolean, string] => {
  const [selectedObjectsCount, allObjectsCount] = data.reduce(
    (acc, curr) => {
      const [selected, overall] = acc;

      return [
        selected + (curr.info.stats?.selectedObjectsCount ?? 0),
        overall + (curr.info.stats?.totalObjectsCount ?? 0),
      ];
    },
    [0, 0]
  );

  const areAllEntityObjectsSelected = selectedObjectsCount === allObjectsCount;
  const areNoneEntityObjectsSelected = !selectedObjectsCount;
  const rootNodeCounter =
    areAllEntityObjectsSelected || areNoneEntityObjectsSelected
      ? ''
      : `${selectedObjectsCount} на карте из ${allObjectsCount}`;

  return [areAllEntityObjectsSelected, rootNodeCounter];
};

export const getRelevanceCriteria = (filterCriterias: FilterCriteria[]) => {
  const relevanceCriteria = filterCriterias.find(
    (criteria) => criteria.name === mapEntityParams.RELEVANCE
  );
  if (relevanceCriteria) {
    return {
      id: relevanceCriteria.id,
      type: relevanceCriteria.type,
      label: relevanceCriteria.label,
      name: relevanceCriteria.name,
      values: [RELEVANT_VALUE, POSSIBLY_RELEVANT_VALUE],
    };
  }
  return undefined;
};
